import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  ImageListItemBar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  CircularProgress,
  Chip,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  DeleteOutline,
  CloudUpload,
  Visibility,
  CheckCircle,
  Cancel,
  Pending,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import {
  showErrorToast,
  showSuccessToast,
} from '../../../../../../components/common/ShowToast';
import { tokens } from '../../../../../../theme';
import apiService from '../../../../../../service/Api';
import { BaseURL } from '../../../../../../service/Api';
// ── NEW: import the compression utility ──
import { compressImage } from '../../../../../../utils/compressImage';

export interface DeceasedGallery {
  id: number;
  deceased: number;
  content_type: string;
  file: string;
  status: 'unapproved' | 'approved' | 'spam';  
  title: string | null;
  description: string | null;
  created_at: string;
  updated_at: string;
  created_by?: string;
  last_updated_by?: string | null;
}

const BASE_PATH = '/api/individuals/deceased-gallery/';

const fetchGallery = (params: {
  deceased?: number;
  search?: string;
  page?: number;
  page_size?: number;
}) =>
  apiService.get<{
    count: number;
    next: string | null;
    previous: string | null;
    results: DeceasedGallery[];
  }>(BASE_PATH, params);

const createGalleryItem = (data: FormData) =>
  apiService.post<DeceasedGallery>(BASE_PATH, data);

const updateGalleryItem = (id: number, data: Partial<DeceasedGallery>) =>
  apiService.patch<DeceasedGallery>(`${BASE_PATH}${id}/`, data, true);

const deleteGalleryItem = (id: number) =>
  apiService.delete(`${BASE_PATH}${id}/`, true);

const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

//check if necessary after changes pushed
const getImageUrl = (path: string) => {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  // Remove trailing slash from base
  const base = BaseURL.replace(/\/$/, '');
  // Ensure path starts with '/'
  const relative = path.startsWith('/') ? path : `/${path}`;
  return `${base}${relative}`;
};

const validateFile = (file: File): string | null => {
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (!ext || !ALLOWED_EXTENSIONS.includes(ext)) {
    return `Unsupported file type. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`;
  }
  if (file.size > MAX_FILE_SIZE) {
    return `File is too large. Maximum size is 10 MB.`;
  }
  return null;
};

interface GalleryTabProps {
  deceasedId: number;
  isAdmin: boolean;
  isMobile: boolean;
  colors: ReturnType<typeof tokens>;
  socketRef?: React.RefObject<WebSocket | null>;
}

export default function GalleryTab({
  deceasedId,
  isAdmin,
  isMobile,
  colors,
  socketRef,
}: GalleryTabProps) {
  const [images, setImages] = useState<DeceasedGallery[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(12);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<number | null>(null);

  const fetchImages = useCallback(
    async (pageNum: number = page) => {
      setLoading(true);
      try {
        const res = await fetchGallery({
          deceased: deceasedId,
          page: pageNum,
          page_size: pageSize,
        });
        setImages(res.data.results);
        setTotalCount(res.data.count);
        setHasNext(!!res.data.next);
        setHasPrev(!!res.data.previous);
        setPage(pageNum);
      } catch (error: any) {
        const msg =
          error?.response?.data?.detail || error?.message || 'Failed to load gallery.';
        showErrorToast(msg, 'gallery-fetch');
      } finally {
        setLoading(false);
      }
    },
    [deceasedId, pageSize, page]
  );

  useEffect(() => {
    if (deceasedId > 0) {
      fetchImages(1);
    } else {
      showErrorToast('Invalid deceased ID', 'invalid-deceased');
      setLoading(false);
    }
  }, [fetchImages, deceasedId]);

  // ── MODIFIED: Upload with frontend compression ──
  const handleUpload = async () => {
    if (!selectedFile) {
      showErrorToast('Please select a file.', 'no-file');
      return;
    }
    const error = validateFile(selectedFile);
    if (error) {
      showErrorToast(error, 'file-validation');
      return;
    }

    setUploading(true);
    try {
      // 1. Compress the image (WebP, resize, target ~3 MB)
      const compressed = await compressImage(selectedFile);

      // 2. Build FormData with the compressed file
      const formData = new FormData();
      formData.append('file', compressed);
      formData.append('deceased', String(deceasedId));
      formData.append('content_type', 'image');
      if (title) formData.append('title', title);
      if (description) formData.append('description', description);
      formData.append('status', 'unapproved');

      // 3. Upload
      await createGalleryItem(formData);
      showSuccessToast('Photo uploaded successfully!');
      setUploadOpen(false);
      setSelectedFile(null);
      setTitle('');
      setDescription('');
      fetchImages(1);
    } catch (error: any) {
      const msg =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'Upload failed. Please try again.';
      showErrorToast(msg, 'upload-error');
      console.error('Upload error:', error?.response?.data || error);
    } finally {
      setUploading(false);
    }
  };

  const confirmDelete = async () => {
    if (imageToDelete === null) return;
    try {
      await deleteGalleryItem(imageToDelete);
      showSuccessToast('Photo deleted.');
      fetchImages(page);
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Delete failed.';
      showErrorToast(msg, 'delete-error');
    } finally {
      setDeleteDialogOpen(false);
      setImageToDelete(null);
    }
  };

  const handleApprove = async (id: number) => {
    try {
      await updateGalleryItem(id, { status: 'approved' });
      showSuccessToast('Photo approved.');
      fetchImages(page);
    } catch (error: any) {
      const msg =
        error?.response?.data?.detail || error?.message || 'Approval failed.';
      showErrorToast(msg, 'approve-error');
    }
  };

  const handleSpam = async (id: number) => {
    try {
      await updateGalleryItem(id, { status: 'spam' });
      showSuccessToast('Marked as spam.');
      fetchImages(page);
    } catch (error: any) {
      const msg =
        error?.response?.data?.detail || error?.message || 'Failed to mark as spam.';
      showErrorToast(msg, 'spam-error');
    }
  };

  const handleUnapprove = async (id: number) => {
    try {
      await updateGalleryItem(id, { status: 'unapproved' });
      showSuccessToast('Unapproved.');
      fetchImages(page);
    } catch (error: any) {
      const msg =
        error?.response?.data?.detail || error?.message || 'Unapprove failed.';
      showErrorToast(msg, 'unapprove-error');
    }
  };

  const visibleImages = isAdmin
    ? images
    : images.filter((img) => img.status === 'approved');

  const useTwoRowLayout = visibleImages.length > 4;
  const gallerySlots: Array<DeceasedGallery | null> = useTwoRowLayout
    ? visibleImages
    : [
        ...visibleImages,
        ...Array.from({ length: Math.max(0, 4 - visibleImages.length) }, () => null),
      ];

  const getMosaicGridRow = (index: number) => {
    // Pattern produces: tall tile, top tile, bottom tile, repeat.
    const position = index % 3;
    if (position === 0) return '1 / span 2';
    if (position === 1) return '1';
    return '2';
  };

  const handleLightboxNext = () =>
    setLightboxIndex((prev) => (prev + 1) % visibleImages.length);
  const handleLightboxPrev = () =>
    setLightboxIndex((prev) => (prev - 1 + visibleImages.length) % visibleImages.length);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Paper
        sx={{
          p: 2,
          boxShadow: 0,
          borderRadius: '20px',
          position: 'relative',
          overflow: 'visible',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography
            sx={{
              fontWeight: 'bold',
              fontSize: { xs: '1rem', sm: '0.7rem', md: '0.7rem' },
            }}
          >
            Gallery
          </Typography>
          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<CloudUpload />}
              onClick={() => setUploadOpen(true)}
              size="small"
              sx={{ borderRadius: '12px', textTransform: 'none' }}
            >
              Upload
            </Button>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : visibleImages.length === 0 ? (
          <Box
            sx={{
              borderRadius: 4,
              height: 300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              backgroundColor: colors.primary[800],
              width: '100%',
            }}
          >
            <CloudUpload sx={{ fontSize: 64, color: colors.primary[400], mb: 2 }} />
            <Typography variant="body1" color="textSecondary">
              No photos yet. {isAdmin ? 'Upload the first one!' : 'Check back later.'}
            </Typography>
            {!isAdmin && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                You can only view and comment on this file. Ask to edit.
              </Typography>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              maxHeight: '600px',
              overflowY: useTwoRowLayout ? 'hidden' : 'auto',
              overflowX: useTwoRowLayout ? 'auto' : 'hidden',
              borderRadius: '12px',
              backgroundColor: colors.primary[800],
              p: 1,
              display: 'grid',
              columnGap: 1,
              rowGap: 1,
              gridAutoFlow: useTwoRowLayout ? 'column' : 'row',
              gridTemplateColumns: useTwoRowLayout
                ? 'none'
                : {
                    xs: 'repeat(2, minmax(120px, 1fr))',
                    sm: 'repeat(4, minmax(150px, 1fr))',
                  },
              gridTemplateRows: useTwoRowLayout
                ? {
                    xs: '130px 130px',
                    sm: '150px 150px',
                  }
                : '220px',
              gridAutoColumns: useTwoRowLayout
                ? {
                    xs: 'minmax(120px, 38vw)',
                    sm: 'minmax(140px, 18vw)',
                    md: 'minmax(150px, 14vw)',
                  }
                : 'auto',
              gridAutoRows: useTwoRowLayout ? 'auto' : '220px',
              alignItems: 'stretch',
            }}
          >
            {gallerySlots.map((img, index) => {
              if (!img) {
                return null;
              }

              return (
                <Box
                  key={img.id}
                  sx={{
                    borderRadius: '12px',
                    overflow: 'hidden',
                    position: 'relative',
                    minHeight: useTwoRowLayout ? 0 : '160px',
                    height: '100%',
                    minWidth: 0,
                    gridRow: useTwoRowLayout ? getMosaicGridRow(index) : 'auto',
                    '&:hover .MuiImageListItemBar-root': {
                      opacity: 1,
                    },
                  }}
                >
                  <img
                    src={getImageUrl(img.file)}
                    alt={img.title || `Photo ${index + 1}`}
                    loading="lazy"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      setLightboxIndex(index);
                      setLightboxOpen(true);
                    }}
                  />
                  <ImageListItemBar
                    title={img.title || ''}
                    subtitle={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>
                          {isAdmin && img.created_by && `by ${img.created_by}`}
                          {img.created_at &&
                            `${
                              isAdmin && img.created_by ? ' · ' : ''
                            }${new Date(img.created_at).toLocaleDateString()}`}
                        </span>
                        {!isAdmin && img.status !== 'approved' && (
                          <Chip
                            label="Unapproved"
                            size="small"
                            icon={<Pending />}
                            sx={{ color: 'white', bgcolor: 'orange' }}
                          />
                        )}
                        {isAdmin && (
                          <Chip
                            label={img.status}
                            size="small"
                            color={
                              img.status === 'approved'
                                ? 'success'
                                : img.status === 'spam'
                                ? 'error'
                                : 'warning'
                            }
                          />
                        )}
                      </Box>
                    }
                    sx={{
                      opacity: 1,
                      transition: 'opacity 0.2s',
                      background:
                        'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
                    }}
                    actionIcon={
                      isAdmin ? (
                        <Box sx={{ display: 'flex', gap: 0.5, pr: 1 }}>
                          {img.status === 'approved' ? (
                            <Tooltip title="Unapprove" placement="top" arrow>
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => handleUnapprove(img.id)}
                              >
                                <Pending fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Approve" placement="top" arrow>
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleApprove(img.id)}
                              >
                                <CheckCircle fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          <Tooltip title="Mark as Spam" placement="top" arrow>
                            <IconButton
                              size="small"
                              sx={{ color: '#EA580C' }}
                              onClick={() => handleSpam(img.id)}
                            >
                              <Cancel fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete" placement="top" arrow>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setImageToDelete(img.id);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <DeleteOutline fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : undefined
                    }
                    position="bottom"
                  />
                </Box>
              );
            })}
          </Box>
        )}

        {totalCount > pageSize && (
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: 'space-between',
              gap: 1.5,
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                textAlign: { xs: 'center', sm: 'left' },
                color: 'text.secondary',
              }}
            >
              Page {page} of {Math.ceil(totalCount / pageSize) || 1}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'row', sm: 'row' },
                justifyContent: { xs: 'space-between', sm: 'flex-end' },
                gap: 1,
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <Button
                variant="outlined"
                onClick={() => fetchImages(page - 1)}
                disabled={!hasPrev}
                fullWidth={isMobile}
                sx={{ minWidth: { xs: '48%', sm: 120 }, borderRadius: '12px', textTransform: 'none' }}
              >
                Previous
              </Button>
              <Button
                variant="contained"
                onClick={() => fetchImages(page + 1)}
                disabled={!hasNext}
                fullWidth={isMobile}
                sx={{ minWidth: { xs: '48%', sm: 120 }, borderRadius: '12px', textTransform: 'none' }}
              >
                Next
              </Button>
            </Box>
          </Box>
        )}

        <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)}>
          <DialogTitle>Upload a Photo</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUpload />}
              >
                Choose File
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setSelectedFile(e.target.files[0]);
                    }
                  }}
                />
              </Button>
              {selectedFile && <Typography variant="body2">{selectedFile.name}</Typography>}
              <TextField
                label="Title (optional)"
                fullWidth
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
              <TextField
                label="Description (optional)"
                fullWidth
                multiline
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <Alert severity="info">After upload, photos will appear once approved by an admin.</Alert>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUploadOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
            >
              {uploading ? <CircularProgress size={24} /> : 'Upload'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          maxWidth={false}
          fullWidth={false}
          scroll="paper"
          sx={{
            '& .MuiDialog-container': {
              alignItems: 'center',
            },
            '& .MuiDialog-paper': {
              overflow: 'hidden',
            },
          }}
          PaperProps={{
            sx: {
              backgroundColor: 'transparent',
              boxShadow: 'none',
              m: 0,
              width: { xs: '95vw', sm: '92vw', md: '88vw' },
              maxWidth: '1100px',
            },
          }}
        >
          <DialogContent
            sx={{
              p: 0,
              position: 'relative',
              height: { xs: '74vh', sm: '78vh', md: '82vh' },
              overflow: 'hidden',
              borderRadius: '0',
              backgroundColor: 'transparent',
            }}
          >
            {visibleImages.length > 0 && (
              <>
                <img
                  src={visibleImages[lightboxIndex].file}
                  alt={visibleImages[lightboxIndex].title || ''}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    p: 2,
                    background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)',
                    color: 'white',
                  }}
                >
                  <Typography variant="body1">
                    {visibleImages[lightboxIndex].title || 'Untitled'}
                  </Typography>
                  <Typography variant="caption">
                    {isAdmin && visibleImages[lightboxIndex].created_by &&
                      `by ${visibleImages[lightboxIndex].created_by}`}
                    {visibleImages[lightboxIndex].created_at &&
                      `${
                        isAdmin && visibleImages[lightboxIndex].created_by ? ' · ' : ''
                      }${new Date(visibleImages[lightboxIndex].created_at!).toLocaleDateString()}`}
                  </Typography>
                </Box>
                {visibleImages.length > 1 && (
                  <>
                    <IconButton
                      onClick={handleLightboxPrev}
                      sx={{
                        position: 'absolute',
                        left: 16,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 42,
                        height: 42,
                        borderRadius: '10px',
                        color: '#7A4E3A',
                        backgroundColor: 'rgba(255, 248, 240, 0.92)',
                        boxShadow: '0 6px 16px rgba(0,0,0,0.18)',
                        '&:hover': { backgroundColor: 'rgba(255, 248, 240, 1)' },
                      }}
                    >
                      {'<'}
                    </IconButton>
                    <IconButton
                      onClick={handleLightboxNext}
                      sx={{
                        position: 'absolute',
                        right: 16,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 42,
                        height: 42,
                        borderRadius: '10px',
                        color: '#7A4E3A',
                        backgroundColor: 'rgba(255, 248, 240, 0.92)',
                        boxShadow: '0 6px 16px rgba(0,0,0,0.18)',
                        '&:hover': { backgroundColor: 'rgba(255, 248, 240, 1)' },
                      }}
                    >
                      {'>'}
                    </IconButton>
                  </>
                )}
                <IconButton
                  onClick={() => setLightboxOpen(false)}
                  sx={{
                    position: 'absolute',
                    right: 16,
                    top: 16,
                    width: 42,
                    height: 42,
                    borderRadius: '50%',
                    color: '#7A4E3A',
                    backgroundColor: 'rgba(255, 248, 240, 0.92)',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.18)',
                    '&:hover': { backgroundColor: 'rgba(255, 248, 240, 1)' },
                  }}
                >
                  ✕
                </IconButton>
              </>
            )}
          </DialogContent>
        </Dialog>
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this photo? This action is permanent and cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" color="error" onClick={confirmDelete}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </motion.div>
  );
}